/**
 * window.oneskin.events — theme analytics (module from layout/theme.liquid, no Vite).
 * Hybrid cart: ns-hybrid-cart/logic/events.ts
 *
 *   track(name, params)    custom events → ElevarDataLayer (no dl_ prefix). Never re-fire Elevar commerce events.
 *   prepare(name, params)  context bag for the next dl_add_to_cart / dl_begin_checkout. Call sync before cart/add.js or checkout.
 *   observeUpsell(el, params)
 *   setCoframeTreatments(treatments)
 *
 * Enrich only: dl_add_to_cart, dl_begin_checkout. Elevar-owned (do not track): view_item, select_item, remove_from_cart, scroll_depth.
 * video_play/pause: control clicks only (playing video in wrapper, not hidden sibling). carousel_advance: forward to a new furthest index only.
 * review_filter / review_sort: Okendo widget selectors only. review_submit / review_abandon out of scope (write flow is a new page).
 * Skio portal view_upsell: #skio-root cards with [data-skio-name="product-description"] + Add (not variant-card Swap/Skip).
 * Skio portal select_upsell: [data-skio-name^="upsell-dropdown-"][data-skio-name$="-option"] (Add only opens the menu).
 * Logs off when the rendered theme role is main; force with oneskin.events.debug = true.
 * Out of scope: Custom Pixel. Elevar must drop existing GTM listeners for nav_click / nav_expand / cta_click.
 */

const INIT_TS = performance?.timeOrigin || Date.now()
const SHOPLIFT_KEY = 'shoplift_subscription_test_variant'
const MOBILE_BREAKPOINT = 768
const REVIEW_FILTER_DEBOUNCE_MS = 400

const CUSTOM_EVENTS = new Set([
  'view_cart',
  'view_upsell',
  'select_upsell',
  'subscription_upgrade',
  'bundle_upgrade',
  'subscription_frequency_select',
  'variant_select',
  'quick_add_click',
  'quick_add_abandon',
  'nav_click',
  'nav_expand',
  'cta_click',
  'accordion_expand',
  'accordion_collapse',
  'carousel_advance',
  'carousel_slide_view',
  'anchor_click',
  'link_click',
  'video_play',
  'video_pause',
  'review_filter',
  'review_sort',
  'promo_code_apply',
  'promo_code_remove',
  'promo_code_error',
  'newsletter_signup',
])

const PREPARE_EVENTS = {
  add_to_cart: 'dl_add_to_cart',
  begin_checkout: 'dl_begin_checkout',
}

const ANCHOR_SECTION_MAP = {
  details: 'details',
  timeline: 'timeline',
  key_ingredients: 'key_ingredients',
  faqs: 'faqs',
  faq: 'faqs',
  ingredients: 'key_ingredients',
  science: 'details',
  'anchor-2': 'details',
  'test-1': 'faqs',
}

const CTA_SELECTOR = [
  '.image-hero__button',
  '.image-with-text-science__button',
  '.rich-text__button',
  '.rich-text__callout-button',
  '.image-with-text-split__text-container-button',
  '.promo-banner__button',
  '.two-link-cards__card',
  '.section-blocks__button a',
  '.section-blocks__button button',
].join(',')

const NAV_LINK_SELECTOR = [
  '[data-navigation] a',
  '[data-secondary-navigation] a',
  '[data-mobile-nav] a',
  '[data-mobile-nav-v2] a',
  '.footer__links a',
  '.footer__bottom-links a',
].join(', ')

const ACCORDION_SELECTOR =
  'details.accordion, details.info-dropdowns__item, details.collapsible-row-list-item, details.collapsible-row'

const CAROUSEL_SOURCES = [
  { selector: '[data-product-mobile-aero-slider]', name: 'pdp_gallery' },
  { selector: '[data-product-protocol-recommendations]', name: 'protocol_recommendations' },
  { selector: '[data-recommended-products-v2-slider]', name: 'longevity_protocol' },
  { selector: '[data-featured-collection-slider-slider]', name: 'featured_collection' },
  { selector: '[data-section-type="timeline-image-slider"]', name: 'results_timeline' },
]

const LINK_CLICK_RE = /explore the science|about the science|see all ingredients|read more/i
const VIDEO_CONTROL_SELECTOR = '[data-video-pause], .js-button-video, [data-play-button-block]'

const pending = {}
const seenSlideViews = new Set()
const seenUpsellViews = new Set()
const carouselMaxIndex = new WeakMap()
let coframeTreatments = []
let lastCartOpen = false
let lastSkioUpsellName
let lastReviewFilter = { value: '', at: 0 }

function themeRole() {
  return document.documentElement?.getAttribute('data-theme-role') || window.Shopify?.theme?.role || ''
}

function isLiveTheme() {
  return themeRole() === 'main'
}

function shouldLog() {
  if (window.oneskin?.events?.debug === true) return true
  if (window.Shopify?.designMode) return true
  return !isLiveTheme()
}

function log(kind, name, payload) {
  if (!shouldLog()) return
  const label = `[oneskin.events] ${kind} ${name}`
  if (console.groupCollapsed) {
    console.groupCollapsed('%c' + label, 'color:#0b6;font-weight:600')
    console.log(payload)
    console.groupEnd()
  } else {
    console.log(label, payload)
  }
}

function deviceType() {
  return window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop'
}

function isProductTemplate() {
  return document.body?.classList.contains('template-product') ?? false
}

function defaultSourceLocation() {
  const classList = document.body?.classList
  if (classList?.contains('template-product')) return 'pdp'
  if (classList?.contains('template-collection')) return 'plp'
  if (classList?.contains('template-search')) return 'plp'
  if (classList?.contains('template-index')) return 'homepage'
  const path = (window.location.pathname || '').toLowerCase()
  if (path.startsWith('/account')) return 'account'
  if (path.startsWith('/collections') || path.startsWith('/search')) return 'plp'
  if (path.startsWith('/products')) return 'pdp'
  return undefined
}

function shopliftVariant() {
  try {
    const cached = sessionStorage.getItem(SHOPLIFT_KEY)
    if (cached) return cached
  } catch {
    /* ignore */
  }
  if (window.shoplift) return 'control'
  return undefined
}

function coframeVariant() {
  if (!coframeTreatments.length) return undefined
  return coframeTreatments.map((t) => `${t.experiment_slug}:${t.variant_slug}`).join('|')
}

function timeToAdd() {
  return Math.round((Date.now() - INIT_TS) / 100) / 10
}

function assignDefined(target, source) {
  if (!source) return target
  for (const [key, value] of Object.entries(source)) {
    if (value != null && value !== '') target[key] = value
  }
  return target
}

function baseParams() {
  return assignDefined(
    { device_type: deviceType() },
    {
      source_location: defaultSourceLocation(),
      shoplift_test_variant: shopliftVariant(),
      coframe_test_variant: coframeVariant(),
    }
  )
}

function mergeParams(overrides) {
  return assignDefined(baseParams(), overrides)
}

function prepare(name, params) {
  const dlName = PREPARE_EVENTS[name] || name
  const next = mergeParams(params)
  if (name === 'add_to_cart') next.time_to_add = timeToAdd()
  pending[dlName] = next
  log('prepare', `${name} → ${dlName}`, next)
}

function consume(dlName) {
  const ctx = pending[dlName]
  delete pending[dlName]
  return ctx || {}
}

function track(name, params) {
  if (!CUSTOM_EVENTS.has(name)) {
    log('skip', name, { reason: 'not in allowlist', params })
    return
  }
  const payload = mergeParams(params)
  payload.event = name
  const dest = typeof window.ElevarDataLayer?.push === 'function' ? 'ElevarDataLayer' : 'dataLayer'
  if (dest === 'ElevarDataLayer') {
    window.ElevarDataLayer.push(payload)
  } else {
    window.dataLayer ||= []
    window.dataLayer.push(payload)
  }
  log('track', name, { dest, payload })
}

function transform(item) {
  if (!item?.event) return item

  if (item.event === 'dl_add_to_cart') {
    const ctx = consume('dl_add_to_cart')
    const products = item.ecommerce?.add?.products || []
    let matched = false
    const ids = window.__nsHybridCartUpsellVariantIds
    if (ids) {
      for (const product of products) {
        const vid = String(product.variant_id || product.id || '')
        if (ids.has(vid)) {
          matched = true
          ids.delete(vid)
        }
      }
    }
    if (matched && !ctx.source_location) ctx.source_location = 'cart_upsell'
    const extra = assignDefined({}, ctx)
    if (ctx.source_location === 'cart_upsell' || matched) extra.hybrid_cart_upsell = true
    const result = { ...item, ...extra }
    log('transform', item.event, { context: ctx, hybrid_cart_upsell: extra.hybrid_cart_upsell === true, result })
    return result
  }

  if (item.event === 'dl_begin_checkout') {
    const checkoutCtx = consume('dl_begin_checkout')
    const result = { ...item, ...checkoutCtx }
    log('transform', item.event, { context: checkoutCtx, result })
    return result
  }

  return item
}

function setCoframeTreatments(treatments) {
  coframeTreatments = (treatments || [])
    .map((t) => ({
      experiment_slug: t.coframe_experiment_slug || t.experiment_slug || '',
      variant_slug: t.coframe_variant_slug || t.variant_slug || '',
    }))
    .filter((t) => t.experiment_slug || t.variant_slug)
}

function textOf(el) {
  return (el?.getAttribute('aria-label') || el?.textContent || '').replace(/\s+/g, ' ').trim()
}

function closestSectionType(el) {
  return el?.closest?.('[data-section-type]')?.getAttribute('data-section-type') || undefined
}

function productFieldsFrom(el) {
  const root =
    el?.closest?.('[data-product-form]') ||
    el?.closest?.('[data-section-type="product"]') ||
    document.querySelector('[data-product-form]')
  if (!root) return {}
  const fields = {}
  if (root.dataset.productTitle) fields.product_name = root.dataset.productTitle
  if (root.dataset.productVariant) fields.product_variant = root.dataset.productVariant
  if (root.dataset.productCategory) fields.product_category = root.dataset.productCategory
  if (root.dataset.itemId) fields.item_id = root.dataset.itemId
  if (root.dataset.isBundle === 'true') fields.is_bundle = true
  if (root.dataset.bundleSize) fields.bundle_size = root.dataset.bundleSize
  return fields
}

function observeUpsell(el, params) {
  if (!el || !window.IntersectionObserver) return
  const key = [params.placement, params.product_name || params.item_id || el.textContent || ''].join(':')
  if (seenUpsellViews.has(key)) return
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || seenUpsellViews.has(key)) continue
        seenUpsellViews.add(key)
        track('view_upsell', params)
        observer.disconnect()
      }
    },
    { threshold: 0.5 }
  )
  observer.observe(el)
}

function markSlideView(componentName, slideIndex, sourceLocation) {
  const key = `${componentName}:${slideIndex}`
  if (seenSlideViews.has(key)) return
  seenSlideViews.add(key)
  track('carousel_slide_view', {
    component_name: componentName,
    slide_index: slideIndex,
    source_location: sourceLocation || defaultSourceLocation(),
  })
}

function carouselSource(el) {
  if (!el) return undefined
  return CAROUSEL_SOURCES.find((source) => el.closest(source.selector))
}

function carouselComponentName(el) {
  return carouselSource(el)?.name || closestSectionType(el) || 'carousel'
}

function isLiveCarousel(el) {
  return !!carouselSource(el)
}

function mapAnchorSection(hash) {
  const raw = String(hash || '')
    .replace(/^#/, '')
    .toLowerCase()
    .replace(/\s+/g, '_')
  if (ANCHOR_SECTION_MAP[raw]) return ANCHOR_SECTION_MAP[raw]
  const match = Object.keys(ANCHOR_SECTION_MAP).find((key) => raw.includes(key))
  return match ? ANCHOR_SECTION_MAP[match] : raw || undefined
}

function navLocation(el) {
  if (el.closest('[data-mobile-nav-accordion-item]')) return 'mobile_accordion'
  if (el.closest('[data-mobile-nav-v2]') || el.closest('[data-mobile-nav]')) return 'mobile_nav'
  if (el.closest('[data-navigation]') || el.closest('[data-secondary-navigation]')) return 'header_primary'
  if (el.closest('.footer__bottom-links')) return 'footer_bottom'
  if (el.closest('.footer__links') || el.closest('[data-section-type="footer"]')) return 'footer_column'
  return 'header_primary'
}

function slideIndexFrom(event) {
  const el = event.target
  return event.detail?.index ?? event.detail?.currentIndex ?? el?.aeroSlider?.currentIndex
}

function onClick(event) {
  const target = event.target
  if (!target?.closest) return

  const videoControl = target.closest(VIDEO_CONTROL_SELECTOR)
  if (videoControl) {
    const video = findVideoForControl(videoControl)
    if (video) {
      track(video.paused ? 'video_play' : 'video_pause', {
        component_name: videoComponentName(video),
        source_location: defaultSourceLocation(),
      })
    } else if (videoControl.matches('[data-play-button-block]')) {
      track('video_play', {
        component_name: closestSectionType(videoControl) || 'play_button_block',
        source_location: defaultSourceLocation(),
      })
    }
  }

  const mobileTab = target.closest('[data-mobile-nav-tab]')
  if (mobileTab && mobileTab.getAttribute('aria-selected') !== 'true') {
    track('nav_expand', { nav_location: 'mobile_tab', device_type: deviceType() })
  }

  const cta = target.closest(CTA_SELECTOR)
  if (cta) {
    track('cta_click', {
      component_name: closestSectionType(cta) || textOf(cta) || 'cta',
      source_location: defaultSourceLocation() || 'homepage',
    })
  }

  const navLink = target.closest(NAV_LINK_SELECTOR)
  if (navLink?.href) {
    track('nav_click', {
      nav_location: navLocation(navLink),
      link_label: textOf(navLink),
      device_type: deviceType(),
    })
  }

  const reviewFilter = target.closest(
    '.oke-w-breakdownModule-distribution-highlight, .oke-reviewsKeywords-list-category'
  )
  if (reviewFilter) emitReviewFilter(reviewFilter, reviewFilterValue(reviewFilter))

  const skioOption = target.closest(
    '[data-skio-name^="upsell-dropdown-"][data-skio-name$="-option"], [data-skio-name^="upsell-dropdown-"][role="menuitemradio"]'
  )
  if (skioOption) {
    const optionName = skioOption.getAttribute('data-skio-name') || ''
    const isSub = optionName.includes('recurring')
    const priceEl = skioOption.querySelector('[data-skio-name="upsell-dropdown-recurring-price"]')
    const priceText = textOf(priceEl || (isSub ? skioOption : null))
    const intervalMatch = priceText && priceText.match(/every\s+\d+\s+days/i)
    track('select_upsell', {
      placement: 'skio',
      product_name: lastSkioUpsellName || skioUpsellProductName(skioUpsellCard(skioOption)),
      source_location: 'skio',
      is_subscription: isSub,
      subscription_interval: intervalMatch ? intervalMatch[0].replace(/\s+/g, '_').toLowerCase() : undefined,
    })
  } else {
    const skioCardClick = skioUpsellCard(target)
    if (skioCardClick) lastSkioUpsellName = skioUpsellProductName(skioCardClick)
  }

  if (isProductTemplate()) {
    const hashLink = target.closest('a[href^="#"]')
    if (hashLink) {
      const section = mapAnchorSection(hashLink.getAttribute('href'))
      if (section) track('anchor_click', { section, source_location: 'pdp' })
    }

    const link = target.closest('a, button, .accordion-drawer__trigger')
    if (link) {
      const label = textOf(link)
      if (LINK_CLICK_RE.test(label) || link.classList?.contains('accordion-drawer__trigger')) {
        track('link_click', {
          component_name: closestSectionType(link) || label || 'link',
          source_location: 'pdp',
        })
      }
    }
  }
}

function onToggle(event) {
  const el = event.target
  if (!el || el.tagName !== 'DETAILS') return

  if (el.matches('[data-mobile-nav-accordion-item], [data-dropdown-trigger], [data-meganav-trigger]')) {
    if (el.open) {
      track('nav_expand', { nav_location: navLocation(el), device_type: deviceType() })
    }
    return
  }

  if (el.matches(ACCORDION_SELECTOR)) {
    const heading = el.querySelector('summary, .accordion__label, .info-dropdowns__trigger')
    const name = textOf(heading) || closestSectionType(el) || 'accordion'
    const loc = el.closest('[data-section-type="info-dropdowns"], [data-section-type="collapsible-row-list"]')
      ? defaultSourceLocation() || 'faq'
      : defaultSourceLocation() || 'pdp'
    track(el.open ? 'accordion_expand' : 'accordion_collapse', {
      component_name: name,
      source_location: loc,
    })
  }
}

function onChange(event) {
  const el = event.target
  if (!el) return

  if (el.matches('.footer__accordion-input')) {
    if (el.checked) {
      track('nav_expand', { nav_location: 'footer_column', device_type: deviceType() })
    }
    return
  }

  if (el.matches('.oke-w-filterOption-checkbox')) {
    const option = el.closest('.oke-w-filterOption') || el
    emitReviewFilter(option, reviewFilterValue(option) || el.getAttribute('name'))
    return
  }

  if (el.matches('select.oke-sortSelect-sort')) {
    const sortLabel = el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex].text : el.value
    track('review_sort', {
      product_name: reviewProductName(),
      sort_value: String(sortLabel || el.value || '')
        .replace(/\s+/g, ' ')
        .trim(),
      reviews_or_questions: reviewsOrQuestions(el),
      source_location: 'pdp',
    })
    return
  }

  if (
    el.matches(
      '[name="product-subscription"], .product-subscription__select, [data-product-subscription-select] select'
    )
  ) {
    let interval = el.value
    if (el.options && el.selectedIndex >= 0) interval = el.options[el.selectedIndex].text || el.value
    if (el.matches('[name="product-subscription"]') && el.value === 'one-time') interval = 'one_time'
    track('subscription_frequency_select', {
      subscription_interval: String(interval || '')
        .replace(/\s+/g, '_')
        .toLowerCase(),
      source_location: 'pdp',
    })
  }
}

function onVariantChange(event) {
  const variant = event.detail?.variant
  const fields = productFieldsFrom(event.target)
  if (variant) {
    if (variant.title) fields.product_variant = variant.title
    if (variant.id) fields.item_id = String(variant.id)
  }
  fields.source_location = 'pdp'
  track('variant_select', fields)
}

function onAeroSlide(event) {
  const el = event.target
  if (!isLiveCarousel(el)) return
  const index = slideIndexFrom(event)
  if (index == null) return
  const prevMax = carouselMaxIndex.get(el) ?? 0
  if (index > prevMax) {
    carouselMaxIndex.set(el, index)
    track('carousel_advance', {
      component_name: carouselComponentName(el),
      source_location: defaultSourceLocation(),
    })
  }
  markSlideView(carouselComponentName(el), index, defaultSourceLocation())
}

function onAeroVisible(event) {
  const el = event.target
  if (!isLiveCarousel(el)) return
  const index = slideIndexFrom(event)
  if (index != null) markSlideView(carouselComponentName(el), index, defaultSourceLocation())
}

function videoComponentName(video) {
  return video.getAttribute('alt') || closestSectionType(video) || 'video'
}

function reviewProductName() {
  const form = document.querySelector('[data-product-form]')
  if (form?.dataset.productTitle) return form.dataset.productTitle
  const oke = document.querySelector('.oke-product-details-name')
  return oke ? textOf(oke) : undefined
}

function reviewsOrQuestions(el) {
  const root = el?.closest?.('[data-oke-widget], .okeReviews, .oke-w') || document
  const tab = root.querySelector('.oke-w-navBar-item[aria-selected="true"]')
  return textOf(tab).toLowerCase().includes('question') ? 'questions' : 'reviews'
}

function emitReviewFilter(el, filterValue) {
  if (!filterValue) return
  const now = Date.now()
  if (filterValue === lastReviewFilter.value && now - lastReviewFilter.at < REVIEW_FILTER_DEBOUNCE_MS) return
  lastReviewFilter = { value: filterValue, at: now }
  track('review_filter', {
    product_name: reviewProductName(),
    filter_value: filterValue,
    reviews_or_questions: reviewsOrQuestions(el),
    source_location: 'pdp',
  })
}

function reviewFilterValue(el) {
  if (!el) return undefined
  const aria = el.getAttribute?.('aria-label')
  if (aria && /filter/i.test(aria)) return aria.replace(/\s+/g, ' ').trim()
  const label = el.matches?.('.oke-w-filterOption') ? el.querySelector('.oke-w-filterOption-label') : el
  return textOf(label || el) || undefined
}

function skioIsAddButton(el) {
  if (!el) return false
  const visible = String(el.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
  return /^add$/i.test(visible)
}

function skioHasAddButton(el) {
  if (!el?.querySelectorAll) return false
  return [...el.querySelectorAll('button')].some(skioIsAddButton)
}

function skioUpsellCard(el) {
  if (!el?.closest) return null
  const root = el.closest('#skio-root')
  if (!root) return null
  if (
    el.closest('[data-skio-name="variant-card-details-container"], [data-skio-name="variant-card-actions-container"]')
  ) {
    return null
  }
  let node = el
  let card = null
  while (node && node !== root) {
    if (node.querySelectorAll?.('[data-skio-name="product-description"]')?.length === 1 && skioHasAddButton(node)) {
      card = node
    }
    node = node.parentElement
  }
  return card
}

function previousSiblingText(start) {
  let node = start
  while (node) {
    const text = textOf(node)
    if (text) return text
    node = node.previousElementSibling
  }
  return undefined
}

function skioUpsellProductName(card) {
  if (!card) return undefined
  for (const named of card.querySelectorAll('[data-skio-name]')) {
    const skioName = named.getAttribute('data-skio-name') || ''
    if (/title|product-name/i.test(skioName) && !skioName.includes('description')) {
      const namedText = textOf(named)
      if (namedText) return namedText
    }
  }
  const desc = card.querySelector('[data-skio-name="product-description"]')
  if (desc) {
    const fromPrev = previousSiblingText(desc.previousElementSibling)
    if (fromPrev) return fromPrev
    const fromGrow = previousSiblingText(desc.parentElement?.previousElementSibling)
    if (fromGrow) return fromGrow
  }
  const img = card.querySelector('img[alt]')
  return img?.alt ? String(img.alt).replace(/\s+/g, ' ').trim() : undefined
}

function scanSkioUpsells(root) {
  if (!root?.querySelectorAll) return
  root.querySelectorAll('[data-skio-name="product-description"]').forEach((desc) => {
    const card = skioUpsellCard(desc)
    if (!card) return
    observeUpsell(card, {
      placement: 'skio',
      product_name: skioUpsellProductName(card),
      source_location: 'skio',
    })
  })
}

function initSkioUpsells() {
  const path = (window.location.pathname || '').toLowerCase()
  if (!path.startsWith('/a/account') && !document.getElementById('skio-root')) return

  function attach(root) {
    scanSkioUpsells(root)
    if (!window.MutationObserver) return
    let timer = null
    const mo = new MutationObserver(() => {
      clearTimeout(timer)
      timer = setTimeout(() => scanSkioUpsells(root), 100)
    })
    mo.observe(root, { childList: true, subtree: true })
  }

  const existing = document.getElementById('skio-root')
  if (existing) {
    attach(existing)
    return
  }
  if (!window.MutationObserver || !document.body) return
  const wait = new MutationObserver(() => {
    const root = document.getElementById('skio-root')
    if (!root) return
    wait.disconnect()
    attach(root)
  })
  wait.observe(document.body, { childList: true, subtree: true })
}

function findVideoForControl(control) {
  const scoped = control.closest('[data-video-wrapper], video-control')
  if (!scoped) return null
  const videos = [...scoped.querySelectorAll('video')]
  if (!videos.length) return null
  return videos.find((video) => !video.paused) || videos[0]
}

function initUpsellImpressions(root) {
  const scope = root?.querySelectorAll ? root : document
  scope
    .querySelectorAll('[data-protocol-tint-add], [data-product-protocol-recommendations] [data-protocol-tint-toggle]')
    .forEach((el) => {
      const card = el.closest('.product-protocol-recommendations__card') || el
      observeUpsell(card, {
        placement: 'protocol',
        product_name: el.getAttribute('data-product-title') || textOf(card),
        source_location: 'pdp',
      })
    })
  scope.querySelectorAll('[data-recommended-products-v2-add]').forEach((el) => {
    const card = el.closest('article') || el.closest('[data-recommended-products-v2-card]') || el
    const loc = el.closest('[data-longevity-account-skio]') ? 'account' : defaultSourceLocation() || 'pdp'
    observeUpsell(card, {
      placement: loc === 'account' ? 'account_new_drop' : 'longevity_protocol',
      product_name: el.getAttribute('data-product-title') || textOf(card),
      source_location: loc,
    })
  })
  scope.querySelectorAll('[data-account-home-new-drop]').forEach((el) => {
    observeUpsell(el, {
      placement: 'account_new_drop',
      product_name: el.getAttribute('data-product-title') || textOf(el),
      source_location: 'account',
    })
  })
}

function initListeners() {
  document.addEventListener('click', onClick, true)
  document.addEventListener('toggle', onToggle, true)
  document.addEventListener('change', onChange, true)
  document.addEventListener('product:variant-change', onVariantChange)
  document.addEventListener('aero:slideChange', onAeroSlide, true)
  document.addEventListener('aero:visible', onAeroVisible, true)
  document.addEventListener('shopify:section:load', (event) => {
    initUpsellImpressions(event.target)
  })
}

function initObservers() {
  initUpsellImpressions(document)
  initSkioUpsells()
}

// Module scripts are deferred. Bind listeners now; wait for DOMContentLoaded so
// upsell observers see DOM that later module scripts build.
initListeners()
if (document.readyState === 'complete') {
  initObservers()
} else {
  document.addEventListener('DOMContentLoaded', initObservers)
}

window.__nsHybridCartUpsellVariantIds ||= new Set()
window.ElevarTransformFn = transform

window.oneskin ||= {}
window.oneskin.events = {
  debug: false,
  track,
  prepare,
  setCoframeTreatments,
  observeUpsell,
  deviceType,
  defaultSourceLocation,
  productFieldsFrom,
  _lastCartOpen(...args) {
    if (args.length) lastCartOpen = args[0]
    return lastCartOpen
  },
}

if (shouldLog()) {
  console.info('%c[oneskin.events] logging on', 'color:#0b6;font-weight:600', {
    theme_role: themeRole() || 'unknown',
    design_mode: !!window.Shopify?.designMode,
  })
}
